import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../service/caller';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { Link, useNavigate } from 'react-router-dom';


const ConsultationDetails = () => {
    const { consultationId } = useParams();
    const [consultationData, setConsultationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [medicalHistoryData, setMedicalHistoryData] = useState(null);
    const [analyses, setAnalyses] = useState([]);
    const [vitalSign, setVitalSign] = useState();
    const [newAnalysis, setNewAnalysis] = useState({ Analysename: '', Analyseresult: '', fileresult: '' });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const pdfContentRef = useRef(null);
    const navigate = useNavigate();
    const [showIframeModal, setShowIframeModal] = useState(false);
    const [iframeUrl, setIframeUrl] = useState('');

    const openFileInIframe = (url) => {
        setIframeUrl(url);
        setShowIframeModal(true);
    };

    const closeIframeModal = () => {
        setShowIframeModal(false); // Close the modal
        setIframeUrl(''); // Clear the iframe URL
    };


    useEffect(() => {
        const fetchSignVitalStatus = async (appointmentId) => {
            try {
                const response = await api.get(`/clinic/signvitalforappointment/${appointmentId}`);
                setVitalSign(response.data.signVitaldata)
                return response.data.signVitaldata;
            } catch (error) {
                console.error('Erreur lors de la vérification des signes vitaux:', error);
                return false; // En cas d'erreur, on suppose qu'il n'y a pas de signes vitaux
            }
        };
        const fetchConsultationDetails = async () => {
            try {
                const response = await api.get(`api/consultations/${consultationId}`);
                setConsultationData(response.data);

                // Récupérer le MedicalHistory associé si disponible
                if (response.data.medicalHistory) {
                    const medHistoryResponse = await api.get(`api/medical_history/${consultationId}`);
                    setMedicalHistoryData(medHistoryResponse.data);

                    // Set analyses if available
                    if (medHistoryResponse.data.Analysesresults && medHistoryResponse.data.Analysesresults.length > 0) {
                        setAnalyses(medHistoryResponse.data.Analysesresults);
                    }
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des détails:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSignVitalStatus(consultationId)
        fetchConsultationDetails();
    }, [consultationId]);

    // Extraire les clés ayant une valeur `true`
    const extractTrueCategories = (analysis) => {
        const result = [];

        // Fonction récursive pour traverser les objets imbriqués
        const traverse = (obj, parentCategory) => {
            for (const [category, value] of Object.entries(obj)) {
                if (typeof value === 'object' && value !== null) {
                    traverse(value, category);
                } else if (value === true) {
                    result.push({ parentCategory, subCategory: category });
                }
            }
        };

        for (const [category, details] of Object.entries(analysis)) {
            traverse(details, category);
        }

        return result;
    };

    const handleOpenModal = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setError('');
        setSuccess('');
        // Reset newAnalysis state when closing modal
        setNewAnalysis({ Analysename: '', Analyseresult: '', fileresult: '' });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setMedicalHistoryData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDeliveryDetailsChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;

        setMedicalHistoryData(prev => ({
            ...prev,
            deliveryDetails: {
                ...prev.deliveryDetails,
                [name]: val
            }
        }));
    };

    const handleNewAnalysisChange = (e) => {
        const { name, value } = e.target;

        // If it's a file input, store both the file and the filename
        if (name === 'fileresult' && e.target.files && e.target.files[0]) {
            setNewAnalysis(prev => ({
                ...prev,
                fileObject: e.target.files[0], // Store actual file object separately
                fileresult: e.target.files[0].name // Store just the name for display
            }));
        } else {
            setNewAnalysis(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const addAnalysis = () => {
        if (!newAnalysis.Analysename || !newAnalysis.Analyseresult) {
            setError('Le nom et le résultat de l\'analyse sont requis');
            return;
        }

        // Check if this analysis already exists (by name)
        const analysisExists = analyses.some(
            analysis => analysis.Analysename === newAnalysis.Analysename
        );

        if (analysisExists) {
            setError(`Une analyse avec le nom "${newAnalysis.Analysename}" existe déjà`);
            return;
        }

        // Add new analysis with temporary ID
        setAnalyses(prev => [...prev, { ...newAnalysis, _id: `temp-${Date.now()}` }]);
        setNewAnalysis({ Analysename: '', Analyseresult: '', fileresult: '' });
        setError('');
    };

    const removeAnalysis = (index) => {
        setAnalyses(prev => prev.filter((_, i) => i !== index));
    };

    const saveMedicalHistory = async () => {
        try {
            setError('');
            setSuccess('');

            // Create FormData object to handle file uploads
            const formData = new FormData();

            // Prepare the analyses data with special handling for files
            const analysesForUpload = analyses.map(analysis => {
                // Create a copy without the fileObject property
                const { fileObject, ...analysisCopy } = analysis;
                return analysisCopy;
            });

            // Prepare the base data (without analyses)
            const { Analysesresults, ...restData } = medicalHistoryData || {};
            const baseData = {
                ...restData,
                ConsultationId: consultationId,
                patientId: consultationData.patientId._id,
            };

            // Add base data to FormData
            formData.append('data', JSON.stringify(baseData));

            // Add analyses data
            formData.append('analyses', JSON.stringify(analysesForUpload));

            // Add files separately
            analyses.forEach((analysis, index) => {
                if (analysis.fileObject) {
                    formData.append(`files[${index}]`, analysis.fileObject, analysis.fileresult);
                }
            });

            let response;

            // If it's a new medical history
            if (!medicalHistoryData._id) {
                response = await api.post('api/medical-history', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            } else {
                // Update existing medical history
                response = await api.put(`api/medical_history/${medicalHistoryData._id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            }

            setMedicalHistoryData(response.data);
            setSuccess('Dossier médical ' + (medicalHistoryData._id ? 'mis à jour' : 'créé') + ' avec succès');

        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            setError('Erreur lors de la sauvegarde du dossier médical');
        }
    };

    // Generate PDF function
    const generatePDF = async () => {
        try {
            // Create a new PDF document
            const doc = new jsPDF('p', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 40;
            let yPos = margin;

            // Add title and header
            doc.setFontSize(20);
            doc.setTextColor(25, 93, 26); // #1A5D1A color
            doc.text("Medical Consultation Report", pageWidth / 2, yPos, { align: 'center' });
            yPos += 30;

            // Add patient details
            doc.setFontSize(14);
            doc.setTextColor(0, 0, 0);
            doc.text(`Patient: ${consultationData.patientId.firstname || 'Not available'}`, margin, yPos);
            doc.text(`Date: ${new Date(consultationData.createdAt).toLocaleDateString()}`, pageWidth - margin, yPos, { align: 'right' });
            yPos += 40;

            // Add horizontal line
            doc.setDrawColor(25, 93, 26); // #1A5D1A color
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 20;

            // Vital signs section
            doc.setFontSize(16);
            doc.setTextColor(25, 93, 26);
            doc.text("Vital Signs", margin, yPos);
            yPos += 25;

            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            if (vitalSign) {
                doc.text(`• Body Temperature: ${vitalSign.bodytemperature || 'N/A'}`, margin + 10, yPos);
                yPos += 20;
                doc.text(`• Blood Pressure: ${vitalSign.bloodPressure || 'N/A'} mmHg`, margin + 10, yPos);
                yPos += 20;
                doc.text(`• Notes: ${vitalSign.details || 'N/A'}`, margin + 10, yPos);
                yPos += 30;
            } else {
                doc.text("No vital signs available", margin + 10, yPos);
                yPos += 30;
            }

            // General questions section
            doc.setFontSize(16);
            doc.setTextColor(25, 93, 26);
            doc.text("General Questions", margin, yPos);
            yPos += 25;

            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            if (consultationData.doctorsquestion && consultationData.doctorsquestion.length > 0) {
                consultationData.doctorsquestion.forEach((q, index) => {
                    // Check if we need a new page
                    if (yPos > pageHeight - 100) {
                        doc.addPage();
                        yPos = margin;
                    }

                    doc.setFontSize(12);
                    doc.setFont(undefined, 'bold');
                    doc.text(`Q: ${q.label}`, margin + 10, yPos);
                    yPos += 20;

                    doc.setFont(undefined, 'normal');
                    doc.text(`R: ${q.content}`, margin + 10, yPos);
                    yPos += 30;
                });
            } else {
                doc.text("No questions available", margin + 10, yPos);
                yPos += 30;
            }

            // Allergies section
            doc.setFontSize(16);
            doc.setTextColor(25, 93, 26);
            doc.text("Allergies / Medical Conditions", margin, yPos);
            yPos += 25;

            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            if (consultationData.allergies && consultationData.allergies.length > 0) {
                consultationData.allergies.forEach((allergy) => {
                    // Check if we need a new page
                    if (yPos > pageHeight - 100) {
                        doc.addPage();
                        yPos = margin;
                    }

                    doc.text(`• ${allergy.label}: ${allergy.response || 'Not specified'}`, margin + 10, yPos);
                    yPos += 20;
                });
            } else {
                doc.text("No allergies recorded", margin + 10, yPos);
                yPos += 20;
            }
            yPos += 10;

            // Laboratory Analysis section
            doc.setFontSize(16);
            doc.setTextColor(25, 93, 26);
            doc.text("Laboratory Analysis", margin, yPos);
            yPos += 25;

            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            if (consultationData.laboratoryAnalysis && consultationData.laboratoryAnalysis.length > 0) {
                consultationData.laboratoryAnalysis.forEach((analysis) => {
                    const trueCategories = extractTrueCategories(analysis);

                    trueCategories.forEach((item) => {
                        // Check if we need a new page
                        if (yPos > pageHeight - 100) {
                            doc.addPage();
                            yPos = margin;
                        }

                        doc.setFont(undefined, 'bold');
                        doc.text(`${item.parentCategory}:`, margin + 10, yPos);
                        yPos += 20;

                        doc.setFont(undefined, 'normal');
                        doc.text(`• ${item.subCategory}`, margin + 30, yPos);
                        yPos += 20;
                    });
                });
            } else {
                doc.text("No laboratory analysis available", margin + 10, yPos);
                yPos += 20;
            }
            yPos += 10;

            // Add new page for Clinical Examinations
            doc.addPage();
            yPos = margin;

            // Clinical Examinations section
            doc.setFontSize(16);
            doc.setTextColor(25, 93, 26);
            doc.text("Clinical Examinations", margin, yPos);
            yPos += 25;

            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            if (consultationData.clinical_exam && consultationData.clinical_exam.length > 0) {
                consultationData.clinical_exam.forEach((exam) => {
                    // Check if we need a new page
                    if (yPos > pageHeight - 100) {
                        doc.addPage();
                        yPos = margin;
                    }

                    doc.setFont(undefined, 'bold');
                    doc.text(`${exam.label}:`, margin + 10, yPos);
                    yPos += 20;

                    doc.setFont(undefined, 'normal');
                    doc.text(`${exam.content}`, margin + 30, yPos);
                    yPos += 30;
                });
            } else {
                doc.text("No clinical examinations recorded", margin + 10, yPos);
                yPos += 30;
            }

            // Diagnosis section
            doc.setFontSize(16);
            doc.setTextColor(25, 93, 26);
            doc.text("Diagnosis", margin, yPos);
            yPos += 25;

            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            if (consultationData.diagnosis && consultationData.diagnosis.length > 0) {
                consultationData.diagnosis.forEach((diag) => {
                    // Check if we need a new page
                    if (yPos > pageHeight - 100) {
                        doc.addPage();
                        yPos = margin;
                    }

                    doc.setFont(undefined, 'bold');
                    doc.text(`${diag.type === 'doctorDiagnosis' ? 'Doctor Diagnosis' : 'AI Diagnosis'}:`, margin + 10, yPos);
                    yPos += 20;

                    doc.setFont(undefined, 'normal');

                    // Split long text into multiple lines
                    const splitText = doc.splitTextToSize(diag.content, pageWidth - (2 * margin) - 40);
                    doc.text(splitText, margin + 30, yPos);
                    yPos += splitText.length * 15 + 15;
                });
            } else {
                doc.text("No diagnosis available", margin + 10, yPos);
                yPos += 30;
            }

            // Add new page for Prescriptions and Medical History
            doc.addPage();
            yPos = margin;

            // Prescriptions section
            doc.setFontSize(16);
            doc.setTextColor(25, 93, 26);
            doc.text("Medical Prescriptions", margin, yPos);
            yPos += 25;

            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            if (consultationData.prescriptions && consultationData.prescriptions.length > 0) {
                consultationData.prescriptions.forEach((pres, index) => {
                    // Check if we need a new page
                    if (yPos > pageHeight - 150) {
                        doc.addPage();
                        yPos = margin;
                    }

                    doc.setDrawColor(40, 167, 69, 0.2); // Light green
                    doc.line(margin + 10, yPos, margin + 15, yPos + 80);

                    doc.setFont(undefined, 'bold');
                    doc.text(`Medication ${index + 1}:`, margin + 20, yPos);
                    yPos += 20;

                    doc.setFont(undefined, 'normal');
                    doc.text(`Name: ${pres.name}`, margin + 30, yPos);
                    yPos += 20;

                    doc.text(`Boxes: ${pres.boxes}`, margin + 30, yPos);
                    yPos += 20;

                    doc.text(`Dosage: ${pres.dosage}`, margin + 30, yPos);
                    yPos += 20;

                    doc.text(`Duration: ${pres.duration}`, margin + 30, yPos);
                    yPos += 30;
                });
            } else {
                doc.text("No prescriptions available", margin + 10, yPos);
                yPos += 30;
            }

            // Follow up section
            if (consultationData.followUp) {
                doc.setFontSize(14);
                doc.setTextColor(25, 93, 26);
                doc.text("Follow Up:", margin, yPos);
                doc.setTextColor(0, 0, 0);
                doc.text(new Date(consultationData.followUp).toLocaleDateString(), margin + 100, yPos);
                yPos += 30;
            }

            // Add Medical History section if available
            if (medicalHistoryData) {
                // Make sure we have enough space, otherwise add a new page
                if (yPos > pageHeight - 200) {
                    doc.addPage();
                    yPos = margin;
                }

                doc.setFontSize(18);
                doc.setTextColor(25, 93, 26);
                doc.text("Medical History", pageWidth / 2, yPos, { align: 'center' });
                yPos += 30;

                doc.setFontSize(14);
                doc.setTextColor(0, 0, 0);

                doc.text(`Health Status: ${medicalHistoryData.healthStatus || 'Not specified'}`, margin, yPos);
                yPos += 25;

                doc.text(`Patient Status: ${medicalHistoryData.patientStatus || 'Not specified'}`, margin, yPos);
                yPos += 25;

                // Delivery details if applicable
                if (medicalHistoryData.deliveryDetails?.hadDelivery) {
                    doc.text("Delivery Details:", margin, yPos);
                    yPos += 20;

                    doc.setFontSize(12);
                    doc.text(`Date: ${medicalHistoryData.deliveryDetails.deliveryDate ? new Date(medicalHistoryData.deliveryDetails.deliveryDate).toLocaleDateString() : 'Not specified'}`, margin + 20, yPos);
                    yPos += 20;

                    doc.text(`Type: ${medicalHistoryData.deliveryDetails.deliveryType || 'Not specified'}`, margin + 20, yPos);
                    yPos += 30;
                }

                // Analysis results if available
                if (analyses && analyses.length > 0) {
                    // Add a new page for analysis results
                    doc.addPage();
                    yPos = margin;

                    doc.setFontSize(16);
                    doc.setTextColor(25, 93, 26);
                    doc.text("Analysis Results", margin, yPos);
                    yPos += 25;

                    // Create table for analyses
                    const tableColumn = ["Analysis Name", "Result", "File"];
                    const tableRows = [];

                    analyses.forEach((analysis) => {
                        const analysisData = [
                            analysis.Analysename || 'Not specified',
                            analysis.Analyseresult || 'Not specified',
                            analysis.fileresult || 'None'
                        ];
                        tableRows.push(analysisData);
                    });

                    autoTable(doc, {
                        head: [tableColumn],
                        body: tableRows,
                        startY: yPos,
                        styles: {
                            overflow: 'linebreak',
                            cellPadding: 3,
                            fontSize: 10
                        },
                        columnStyles: {
                            0: { cellWidth: 150 },
                            1: { cellWidth: 200 },
                            2: { cellWidth: 150 }
                        },
                        theme: 'grid',
                        headStyles: {
                            fillColor: [25, 93, 26],
                            textColor: [255, 255, 255],
                            fontStyle: 'bold'
                        }
                    });

                    yPos = doc.lastAutoTable.finalY + 20;
                }
            }

            // Add footer with date and page numbers
            const totalPages = doc.internal.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFontSize(10);
                doc.setTextColor(150);
                doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, pageHeight - 20);
                doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 20, { align: 'right' });
            }

            // Save the PDF
            doc.save(`Consultation_${consultationId}_${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        }
    };

    if (loading) {
        return <p>Chargement...</p>;
    }

    const { medicalHistory, allergies, diagnosis, prescriptions, followUp, clinical_exam, laboratoryAnalysis, services, servicesrecommandes, aisuggestion } = consultationData;

    // Exemple de styles pour l'iframe
    const iframeStyles = {
        border: 'none',
        width: '90%',
        maxHeight: '80vh',
        borderRadius: '8px',
    }

    return (
        <div className="position-relative">
            <div className="bg-white p-5">
                <div>
                    <button className="btn btn-prim rounded-pill py-2 px-4" onClick={() => navigate(-1)} style={{ color: '#0056B3', border: '1.5px solid #0056B3' }}>
                        <i className="bi bi-arrow-left me-2"></i>Retour
                    </button>
                </div>

                {/* Détails du patient */}
                <div className="text-white mt-3 p-3 row" style={{ background: '#1A5D1A', borderRadius: '15px' }}>
                    <div className="col-12 col-md-5 mx-auto mb-3">
                        <p>Name :</p>
                        <br />
                        <h3>{consultationData.patientId.firstname || 'Nom non disponible'}</h3>
                    </div>
                    <div className="col-md-3"></div>
                    <div className="col-12 col-md-4 mx-auto mb-3">
                        <p className="text-center text-md-end">Appointment of</p>
                        <br />
                        <h3 className="text-center text-md-end">{new Date(consultationData.createdAt).toLocaleDateString()}</h3>
                        <p className="text-center text-md-end">{new Date(consultationData.createdAt).toLocaleTimeString()}</p>
                    </div>
                </div>

                {/* Vital signs */}
                <div className="mt-3 p-3" style={{ background: '#28A7452B', borderRadius: '10px' }}>
                    <strong>Vital signs</strong>
                </div>
                <div className="mt-3 ms-3">
                    <ul>
                        <li>Body Temperature : <strong style={{ color: '#EF3826' }}>{vitalSign?.bodytemperature}</strong></li>
                        <li>Blood pressure : <strong>{vitalSign?.bloodPressure} mmHg</strong></li>
                    </ul>
                    <p>Note : <strong>{vitalSign?.details}</strong></p>
                </div>

                {/* General questions */}
                <div className="mt-3 p-3" style={{ background: '#28A7452B', borderRadius: '10px' }}>
                    <strong>General questions</strong>
                </div>
                <div className="mt-3 ms-3">
                    {consultationData.doctorsquestion && consultationData.doctorsquestion.map((q, index) => (
                        <div key={index}>
                            <div className="row mt-3">
                                <div className="col-1 mx-auto">
                                    <strong>Q :</strong>
                                </div>
                                <div className="col-11 mx-auto">
                                    <p>{q.label}</p>
                                </div>
                            </div>
                            <div className="row mt-3">
                                <div className="col-1 mx-auto align-self-center">
                                    <strong>R :</strong>
                                </div>
                                <div className="col-11 mx-auto">
                                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                        <li>{q.content}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Maladies / Allergies */}
                <div className="mt-3 p-3" style={{ background: '#28A7452B', borderRadius: '10px' }}>
                    <strong>Maladies / Allergies</strong>
                </div>
                <div className="mt-3 ms-3">
                    {allergies && allergies.map((allergy, index) => (
                        <p key={index}>{allergy.label} : <strong>{allergy.response || 'Non renseigné'}</strong></p>
                    ))}
                </div>

                {/* Affichage des analyses de laboratoire */}
                <div className="mt-3 p-3" style={{ background: '#28A7452B', borderRadius: '10px' }}>
                    <strong>Laboratory Analysis</strong>
                </div>
                <div className="mt-3 ms-3">
                    <ul>
                        {laboratoryAnalysis && laboratoryAnalysis.map((analysis, index) => {
                            const trueCategories = extractTrueCategories(analysis);

                            return (
                                <div key={index}>
                                    {trueCategories.map((item, idx) => (
                                        <div key={idx}>
                                            <p><strong>{item.parentCategory}:</strong></p>
                                            <ul>
                                                <li>{item.subCategory}</li>
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </ul>
                </div>
                {/* Clinical Examinations */}
                <div className="mt-3 p-3" style={{ background: '#28A7452B', borderRadius: '10px' }}>
                    <strong>Clinical examinations</strong>
                </div>
                <div className="mt-3">
                    {clinical_exam && clinical_exam.map((exam, index) => (
                        <div key={index} className="row mt-3 ms-3">
                            <div className="col-3 mx-auto align-self-center">
                                <strong>{exam.label}:</strong>
                            </div>
                            <div className="col-9 mx-auto">
                                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                    <li>{exam.content}</li>
                                    {exam.file && (
                                        <li>
                                            <a
                                                href={exam.file} 
                                                target="_blank" 
                                                rel="noopener noreferrer" // Sécuriser le lien
                                                style={{ color: '#28A745', textDecoration: 'underline' }} 
                                            >
                                                Voir le fichier
                                            </a>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>


                {/* Iframe Modal pour afficher les fichiers */}
                {showIframeModal && (
                    <div className="iframe-modal">
                        <div className="iframe-content">
                            <button onClick={closeIframeModal} >X</button>
                            <iframe
                                src={iframeUrl}
                                title="File Viewer"
                                width="100%"
                                height="600px"
                                style={iframeStyles}
                                frameBorder="0"
                            />
                        </div>
                    </div>
                )}


                {/* Diagnosis */}
                <div className="mt-3 p-3" style={{ background: '#28A7452B', borderRadius: '10px' }}>
                    <strong>Diagnosis</strong>
                </div>
                <div className="mt-3 ms-3">
                    {diagnosis && diagnosis.map((diag, index) => (
                        <p key={index}>
                            <strong style={{ textDecoration: 'underline' }}>{diag.type === 'doctorDiagnosis' ? 'Doctor Diagnosis' : 'AI Diagnosis'}</strong>
                            <br />
                            {diag.content}
                        </p>
                    ))}
                </div>

                {/* Prescriptions */}
                <div className="mt-3 p-3" style={{ background: '#28A7452B', borderRadius: '10px' }}>
                    <strong>Medical prescriptions</strong>
                </div>
                <div className="mt-3 ms-3">
                    {prescriptions && prescriptions.map((pres, index) => (
                        <div key={index} className="mt-3 ms-3" style={{ borderLeft: '1px solid #28A745' }}>
                            <div className="d-flex ms-2">
                                <div><strong>Name :</strong></div>
                                <div className="ms-2">{pres.name}</div>
                            </div>
                            <div className="d-flex ms-2">
                                <div><strong>Nbr de boites :</strong></div>
                                <div className="ms-2">{pres.boxes}</div>
                            </div>
                            <div className="d-flex ms-2">
                                <div className="align-self-center"><strong>Prises :</strong></div>
                                <div>
                                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                        <li>{pres.dosage}</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="d-flex ms-2 mt-3">
                                <div><strong>Durée  :</strong></div>
                                <div className="ms-2">{pres.duration}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Service offert */}
                <div className="mt-3 p-3" style={{ background: '#28A7452B', borderRadius: '10px' }}>
                    <strong>Service Offerts</strong>
                </div>
                <div className="mt-3 ms-3">
                    {services && services.map((service, index) => (
                        <div key={index} className="mt-3 ms-3" style={{ borderLeft: '1px solid #28A745' }}>
                            <div className="d-flex ms-2">
                                <div><strong>Name :</strong></div>
                                <div className="ms-2">{service.name}</div>
                            </div>
                            <div className="d-flex ms-2">
                                <div><strong>Prices :</strong></div>
                                <div className="ms-2">{service.price} {consultationData.hospitalId.hospital_currency}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Service recommande */}
                {servicesrecommandes?.length > 0 && (
                    <>
                        <div
                            className="mt-3 p-3"
                            style={{ background: '#28A7452B', borderRadius: '10px' }}
                        >
                            <strong>Service Recommandé</strong>
                        </div>
                        <div className="mt-3 ms-3">
                            {servicesrecommandes.map((service, index) => (
                                <div
                                    key={index}
                                    className="mt-3 ms-3"
                                    style={{ borderLeft: '1px solid #28A745' }}
                                >
                                    <div className="d-flex ms-2">
                                        <strong>Name :</strong>
                                        <span className="ms-2">{service.name}</span>
                                    </div>
                                    <div className="d-flex ms-2">
                                        <strong>Prices :</strong>
                                        <span className="ms-2">
                                            {service.price} {consultationData.hospitalId.hospital_currency}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}


                {/* ai suggestions */}
                {aisuggestion && aisuggestion.trim() !== '' && (
                    <div className="d-flex ms-2 mt-4">
                        <strong style={{ color: '#269226' }}>AI suggestion :</strong>
                        <br></br>
                        <div className="ms-3">
                            <p>{aisuggestion}</p>
                        </div>
                    </div>
                )}


                {/* Follow up */}
                <div className="d-flex ms-2 mt-4" >
                    <div><strong style={{ color: '#269226' }}>Follow up :</strong></div>
                    <div className="ms-3">
                        <p>{new Date(followUp).toLocaleDateString()}</p>
                    </div>
                </div>

                <div className="mt-2 d-flex justify-content-end">
                    <button type="button" className="btn btn-prim text-white fw-bold px-4 py-2" onClick={generatePDF} style={{ background: '#1A5D1A' }}>
                        Export to pdf <i className="bi bi-arrow-down-square ms-2 fw-bold"></i>
                    </button>
                    <button type="button" className="btn btn-primary text-white fw-bold px-4 py-2 mx-2" onClick={handleOpenModal}>
                        Ajouter notes <i className="bi bi-arrow-down-square ms-2 fw-bold"></i>
                    </button>
                </div>
            </div>

            {/* Modal pour modifier le MedicalHistory */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton style={{ background: '#28A7452B' }}>
                    <Modal.Title>Modifier le dossier médical</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}

                    {medicalHistoryData && (
                        <Form>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Statut de santé</Form.Label>
                                        <Form.Select
                                            name="healthStatus"
                                            value={medicalHistoryData.healthStatus || 'Non critique'}
                                            onChange={handleInputChange}
                                        >
                                            <option value="Critique">Critique</option>
                                            <option value="Non critique">Non critique</option>
                                            <option value="Stable">Stable</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Statut du patient</Form.Label>
                                        <Form.Select
                                            name="patientStatus"
                                            value={medicalHistoryData.patientStatus || 'En traitement'}
                                            onChange={handleInputChange}
                                        >
                                            <option value="Vivant">Vivant</option>
                                            <option value="Décédé">Décédé</option>
                                            <option value="Guéri">Guéri</option>
                                            <option value="En traitement">En traitement</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <div className="mt-4 mb-3">
                                <h5>Détails de l'accouchement</h5>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                label="A accouché"
                                                name="hadDelivery"
                                                checked={medicalHistoryData.deliveryDetails?.hadDelivery || false}
                                                onChange={handleDeliveryDetailsChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    {medicalHistoryData.deliveryDetails?.hadDelivery && (
                                        <>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Date d'accouchement</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="deliveryDate"
                                                        value={medicalHistoryData.deliveryDetails?.deliveryDate ? new Date(medicalHistoryData.deliveryDetails.deliveryDate).toISOString().split('T')[0] : ''}
                                                        onChange={handleDeliveryDetailsChange}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label>Type d'accouchement</Form.Label>
                                                    <Form.Select
                                                        name="deliveryType"
                                                        value={medicalHistoryData.deliveryDetails?.deliveryType || 'Autre'}
                                                        onChange={handleDeliveryDetailsChange}
                                                    >
                                                        <option value="Césarienne">Césarienne</option>
                                                        <option value="Vaginale">Vaginale</option>
                                                        <option value="Autre">Autre</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </div>

                            <div className="mt-4 mb-3">
                                <h5>Résultats d'analyses</h5>
                                <div className="mb-3">
                                    {analyses.map((analysis, index) => (
                                        <div key={index} className="card p-3 mb-2">
                                            <div className="d-flex justify-content-between">
                                                <h6>{analysis.Analysename}</h6>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => removeAnalysis(index)}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </Button>
                                            </div>
                                            <p><strong>Résultat:</strong> {analysis.Analyseresult}</p>
                                            {analysis.fileresult && <p><strong>Fichier:</strong> {analysis.fileresult}</p>}
                                        </div>
                                    ))}
                                </div>

                                <div className="card p-3">
                                    <h6>Ajouter une analyse</h6>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Nom de l'analyse</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="Analysename"
                                                    value={newAnalysis.Analysename}
                                                    onChange={handleNewAnalysisChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Résultat</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="Analyseresult"
                                                    value={newAnalysis.Analyseresult}
                                                    onChange={handleNewAnalysisChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Fichier</Form.Label>
                                                <Form.Control
                                                    type="file"
                                                    name="fileresult"
                                                    onChange={handleNewAnalysisChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <div className="text-end">
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={addAnalysis}
                                        >
                                            Ajouter cette analyse
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label>Date de consultation</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="consultationDate"
                                    value={medicalHistoryData.consultationDate ? new Date(medicalHistoryData.consultationDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Fermer
                    </Button>
                    <Button variant="primary" onClick={saveMedicalHistory} style={{ background: '#1A5D1A', borderColor: '#1A5D1A' }}>
                        Enregistrer
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ConsultationDetails;